import { Component, computed, effect, inject, input, InputSignal, signal } from "@angular/core";
import { OnBackDirective } from "@bitwarden/web-vault/app/directives/onBack.directive";
import { View } from "@bitwarden/web-vault/app/models/enum/view.enum";
import { ViewsService } from "@bitwarden/web-vault/app/services/views/views.service";
import { InstitutionOriginEnum } from "@bitwarden/web-vault/app/models/types/institution.type";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { CategorySourceEnum } from "@bitwarden/web-vault/app/models/types/category.types";
import { ConfirmationEnum } from "@bitwarden/web-vault/app/models/enum/confirmation.enum";
import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { BudgetStoreService } from "@bitwarden/web-vault/app/services/store/budget/budget.store.service";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { CategoryUtils } from "@bitwarden/web-vault/app/models/view/category/category.utils";

@Component({
  selector: "app-categories-details",
  templateUrl: "./categories-details.component.html",
})
export class CategoriesDetailsComponent extends OnBackDirective {
  private viewsService: ViewsService = inject(ViewsService);
  private transactionStoreService: TransactionStoreService = inject(TransactionStoreService);
  private userStoreService: UserStoreService = inject(UserStoreService);
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private i18nService = inject(I18nService);
  private calculatedPastStore: CalculationPastStoreService = inject(CalculationPastStoreService);
  private budgetStoreService: BudgetStoreService = inject(BudgetStoreService);

  private categoryUtils = new CategoryUtils();

  protected categorySource = CategorySourceEnum;
  protected isDeleting = signal(false);
  protected isSavingName = signal(false);
  // holds an in-progress edit, cleared whenever the selected category changes so it
  // doesn't leak into the newly selected category
  private editedName = signal<string | null>(null);

  protected selectedCategoryId: InputSignal<string> = input("");
  selectedCategory = computed(() =>
    this.userStoreService.categories
      .categoryViews()
      .find((cat) => cat.id === this.selectedCategoryId()),
  );

  private clearEditedNameOnCategoryChange = effect(
    () => {
      this.selectedCategoryId();
      this.editedName.set(null);
    },
    { allowSignalWrites: true },
  );

  source = computed(() => {
    return this.selectedCategory().source;
  });

  categoryNameValue = computed(() => {
    const edited = this.editedName();
    return edited ?? this.selectedCategory()?.name ?? "";
  });

  linkedTransactionCount = computed(() => {
    let count = 0;
    this.transactionStoreService.enrichTransaction.collection().filter((transaction) => {
      if (transaction.c_categories.includes(this.selectedCategory().id)) {
        count++;
      }
    });
    return count;
  });

  linkedBudgetsCount = computed(() => {
    const budgets = this.budgetStoreService.budget.budgetViews() ?? [];
    const selectedId = this.selectedCategory().id;
    return budgets.reduce((acc, budget) => {
      return budget.categoriesLink.some((link) => link.id === selectedId) ? acc + 1 : acc;
    }, 0);
  });

  goToPastView(): void {
    this.calculatedPastStore.filters.filterOnCategories(this.selectedCategory());
    this.viewsService.view = View.PAST;
  }

  async deleteCategory() {
    if (this.linkedTransactionCount() > 0) {
      this.notifyService.notify(
        NotificationEnum.warning,
        "notice",
        `${this.i18nService.t("categoryCannotDeleteWhenLinkedTransactionsExist", this.linkedTransactionCount())}`,
        false,
      );
      return;
    }

    if (this.linkedBudgetsCount() > 0) {
      this.notifyService.notify(
        NotificationEnum.warning,
        "notice",
        `${this.i18nService.t("categoryCannotDeleteWhenLinkedBudgetsExist", this.linkedBudgetsCount())}`,
        false,
      );
      return;
    }

    const confirmed = await this.notifyService.confirmFor(ConfirmationEnum.deletingCategory);
    if (!confirmed) {
      return;
    }

    this.isDeleting.set(true);
    this.userStoreService.categories
      .delete(this.selectedCategory())
      .then((r) => {
        this.notifyService.notify(NotificationEnum.success, "succeeded", "deletedSuccessfully");
        this.handleBackClick();
      })
      .catch(() => {
        this.isDeleting.set(false);
      });
    return;
  }

  onCategoryNameChange(name: string) {
    this.editedName.set(name);
  }

  async saveCategoryName() {
    if (this.isSavingName()) {
      return;
    }

    const category = this.selectedCategory();
    const name = this.categoryNameValue().trim();

    if (!name) {
      this.notifyService.notify(NotificationEnum.warning, "notice", "categoryNameInfo");
      return;
    }

    if (name === category.name) {
      this.notifyService.notify(NotificationEnum.warning, "notice", "categoryNameUnchanged");
      return;
    }

    const confirmed = await this.notifyService.confirmFor(ConfirmationEnum.renamingCategory, {
      messageReplacement: `Rename "${category.name}" to "${name}"?`,
    });
    if (!confirmed) {
      return;
    }

    this.isSavingName.set(true);
    try {
      const updated = await this.categoryUtils.updateCategoryName(
        name,
        category.clone() as CategoryView,
        this.userStoreService,
      );
      if (updated) {
        this.notifyService.notify(NotificationEnum.success, "succeeded", "updatedSuccessfully");
      }
    } catch (e) {
      this.notifyService.notify(
        NotificationEnum.error,
        "errorOccurred",
        (e as Error).message,
        true,
      );
    } finally {
      this.editedName.set(null);
      this.isSavingName.set(false);
    }
  }

  protected readonly InstitutionOriginEnum = InstitutionOriginEnum;
}
